import React, { useEffect, useState } from 'react';
import tokenService from '../../services/tokenService';
import { Copy, Trash2, Pencil, X as LucideX, Search } from 'lucide-react';
import { jwtDecode } from 'jwt-decode';
import { defaultPermissions, serviceLabels, expirationOptions } from '../../utils/tokenConfig';

const EditTokenModal = ({ open, onClose, token, onUpdated }) => {
  const [formData, setFormData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [removedPermissions, setRemovedPermissions] = useState([]);

  useEffect(() => {
    if (token) {
      try {
        const decoded = jwtDecode(token.token);
        const mergedPermissions = { ...defaultPermissions };
        let removed = [];
        if (decoded.permissions) {
          for (const key of Object.keys(decoded.permissions)) {
            if (mergedPermissions[key]) {
              mergedPermissions[key] = { ...mergedPermissions[key], ...decoded.permissions[key] };
            } else {
              removed.push(key);
            }
          }
        }
        setFormData({
          name: token.name,
          tokenId: decoded.id,
          expiresIn: '',
          permissions: mergedPermissions
        });
        setRemovedPermissions(removed);
      } catch (e) {
        setFormData(null);
        setRemovedPermissions([]);
      }
    }
  }, [token]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handlePermissionChange = (service, operation) => {
    setFormData(prev => ({
      ...prev,
      permissions: {
        ...prev.permissions,
        [service]: {
          ...prev.permissions[service],
          [operation]: !prev.permissions[service][operation]
        }
      }
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      // Eliminar permisos que no estén en defaultPermissions antes de enviar
      const cleanPermissions = {};
      Object.keys(defaultPermissions).forEach(service => {
        cleanPermissions[service] = formData.permissions[service];
      });
      const cleanFormData = { ...formData, permissions: cleanPermissions };
      await tokenService.updateToken(token.id, cleanFormData);
      if (onUpdated) onUpdated();
      setTimeout(() => onClose(), 1000);
    } catch (err) {
      setError(err.response?.data?.message || 'Error al actualizar el token');
    } finally {
      setLoading(false);
    }
  };

  if (!open || !formData) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
      <div className="bg-white rounded-lg shadow-lg w-full max-w-2xl relative flex flex-col max-h-[90vh]">
        <button
          onClick={onClose}
          className="absolute top-2 right-4 text-white bg-red-600 hover:bg-red-800 rounded-full w-10 h-10 flex items-center justify-center text-2xl shadow-lg border-2 border-white"
          title="Cerrar"
        >
          <LucideX size={28} />
        </button>
        <div className="overflow-y-auto p-8 flex-1">
          <h2 className="text-xl font-semibold text-gray-800 mb-5 flex items-center">Editar Token</h2>
          {removedPermissions.length > 0 && (
            <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-4 text-yellow-800">
              <strong>Advertencia:</strong> Este token tiene permisos que ya no existen: <span className="font-bold">{removedPermissions.join(', ')}</span>. Estos permisos serán eliminados al actualizar el token.
            </div>
          )}
          {error && <div className="bg-red-50 border-l-4 border-red-400 p-4 mb-4 text-red-700">{error}</div>}
          <form onSubmit={handleSubmit} className="space-y-8">
            <div className="bg-white rounded-lg shadow-md p-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">Nombre del Token</label>
                  <input type="text" name="name" value={formData.name} onChange={handleInputChange} className="w-full px-4 py-2 rounded-md border border-gray-300 focus:ring-blue-500 focus:border-blue-500" required />
                </div>
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">ID del Token</label>
                  <input type="text" name="tokenId" value={formData.tokenId} onChange={handleInputChange} className="w-full px-4 py-2 rounded-md border border-gray-300 focus:ring-blue-500 focus:border-blue-500" required />
                </div>
              </div>
            </div>
            <div className="bg-white rounded-lg shadow-md p-6">
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">Tiempo de validez</label>
                <select name="expiresIn" value={formData.expiresIn} onChange={handleInputChange} className="w-full px-4 py-2 rounded-md border border-gray-300 focus:ring-blue-500 focus:border-blue-500">
                  <option value="">Sin cambio</option>
                  {expirationOptions.map(option => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-xl font-semibold text-gray-800 mb-5 flex items-center">Permisos</h2>
              {Object.entries(formData.permissions).map(([service, operations]) => (
                <div key={service} className="mb-6 border-b pb-6 last:border-0">
                  <h3 className="text-lg font-medium text-gray-700 mb-3">{serviceLabels[service]}</h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {Object.entries(operations).map(([operation, value]) => (
                      <div key={`${service}-${operation}`} className="flex items-center space-x-2">
                        <label className="inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={value}
                            onChange={() => handlePermissionChange(service, operation)}
                            className="sr-only peer"
                          />
                          <div className="relative w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                          <span className="ml-2 text-sm font-medium text-gray-700 capitalize">{operation}</span>
                        </label>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
            <div className="flex justify-end items-center">
              <button type="submit" disabled={loading} className="px-6 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50">
                {loading ? 'Actualizando...' : 'Actualizar Token'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

const TokenList = () => {
  const [tokens, setTokens] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copiedId, setCopiedId] = useState(null);
  const [editToken, setEditToken] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [recordsPerPage, setRecordsPerPage] = useState(5);

  useEffect(() => {
    const fetchTokens = async () => {
      try {
        const data = await tokenService.getTokens();
        setTokens(data);
      } catch (error) {
        console.error('Error al cargar tokens:', error);
        setTokens([]);
      } finally {
        setLoading(false);
      }
    };
    fetchTokens();
  }, []);

  const handleDelete = async (id) => {
    try {
      await tokenService.deleteToken(id);
      setTokens(tokens.filter(t => t.id !== id));
    } catch (error) {
      console.error('Error al eliminar token:', error);
    }
  };

  const handleCopy = async (token) => {
    try {
      await navigator.clipboard.writeText(token.token);
      setCopiedId(token.id);
      setTimeout(() => setCopiedId(null), 2000);
    } catch (error) {
      console.error('Error al copiar token:', error);
    }
  };

  const handleEdit = (token) => {
    setEditToken(token);
  };

  const handleModalClose = () => {
    setEditToken(null);
  };

  const handleTokenUpdated = async () => {
    // Refrescar la lista de tokens
    const data = await tokenService.getTokens();
    setTokens(data);
    setEditToken(null); // Cerrar el modal inmediatamente
  };

  // Filtrar tokens basado en el término de búsqueda
  const filteredTokens = tokens.filter(token =>
    token.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    token.id.toString().includes(searchTerm) ||
    token.token.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Calcular paginación
  const indexOfLastRecord = currentPage * recordsPerPage;
  const indexOfFirstRecord = indexOfLastRecord - recordsPerPage;
  const currentRecords = filteredTokens.slice(indexOfFirstRecord, indexOfLastRecord);
  const totalPages = Math.ceil(filteredTokens.length / recordsPerPage);

  if (loading) return <div className="text-gray-500">Cargando tokens...</div>;

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <h2 className="text-xl font-semibold text-gray-800 mb-6">Tokens Generados</h2>
      <EditTokenModal open={!!editToken} onClose={handleModalClose} token={editToken} onUpdated={handleTokenUpdated} />

      {/* Buscador */}
      <div className="mb-4">
        <div className="relative">
          <input
            type="text"
            placeholder="Buscar tokens..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-blue-500 focus:border-blue-500"
          />
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
        </div>
      </div>

      {/* Selector de registros por página */}
      <div className="mb-4 flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <label className="text-sm text-gray-600">Mostrar</label>
          <select
            value={recordsPerPage}
            onChange={(e) => {
              setRecordsPerPage(Number(e.target.value));
              setCurrentPage(1);
            }}
            className="border rounded px-3 py-1 text-sm pr-8 appearance-none bg-white"
          >
            <option value={5}>5</option>
            <option value={10}>10</option>
            <option value={15}>15</option>
            <option value={20}>20</option>
          </select>
          <span className="text-sm text-gray-600">registros</span>
        </div>
      </div>

      {filteredTokens.length === 0 ? (
        <div className="text-gray-500">No hay tokens que coincidan con la búsqueda.</div>
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ID</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Nombre</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Token</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Acciones</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {currentRecords.map(token => (
                  <tr key={token.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{token.id}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{token.name}</td>
                    <td className="px-6 py-4 text-sm text-gray-500"><div className="max-w-md truncate">{token.token}</div></td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex justify-end space-x-2">
                        <button
                          onClick={() => handleCopy(token)}
                          className="text-blue-600 hover:text-blue-900 p-1 rounded-full hover:bg-blue-50"
                          title="Copiar token"
                        >
                          <Copy size={18} />
                          {copiedId === token.id && (
                            <span className="absolute -mt-8 bg-green-100 text-green-800 text-xs px-2 py-1 rounded">Copiado!</span>
                          )}
                        </button>
                        <button
                          onClick={() => handleEdit(token)}
                          className="text-yellow-600 hover:text-yellow-900 p-1 rounded-full hover:bg-yellow-50"
                          title="Editar token"
                        >
                          <Pencil size={18} />
                        </button>
                        <button
                          onClick={() => handleDelete(token.id)}
                          className="text-red-600 hover:text-red-900 p-1 rounded-full hover:bg-red-50"
                          title="Eliminar token"
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Paginación */}
          <div className="flex items-center justify-between mt-4 text-sm text-gray-600">
            <div>
              Mostrando {currentRecords.length} de {filteredTokens.length} registros
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                className="px-3 py-1 bg-blue-500 text-white border rounded disabled:opacity-50"
              >
                Anterior
              </button>
              <span>Página {currentPage} de {totalPages}</span>
              <button
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
                className="px-3 py-1 bg-blue-500 text-white border rounded disabled:opacity-50"
              >
                Siguiente
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default TokenList; 